// Copyright (C) 2025 Skylark Drones

import { CAMERA_LENS } from './camera-control'

export const NETWORK_QUALITY = {
  NO_SIGNAL: 0,
  VERY_POOR: 1,
  POOR: 2,
  FAIR: 3,
  GOOD: 4,
  EXCELLENT: 5
}

export const NETWORK_TYPE = {
  '4G': 1,
  ETHERNET: 2
}

export const DOCK_POWER_SOURCE = {
  AC: 'ac',
  BACKUP: 'backup'
}

export const CAMERA_ISO = {
  0: 'Auto',
  1: 'Auto(high sense)',
  2: '50',
  3: '100',
  4: '200',
  5: '400',
  6: '800',
  7: '1600',
  8: '3200',
  9: '6400',
  10: '12800',
  11: '25600',
  255: 'Fixed'
}

export const CAMERA_EXPOSURE_VALUE = {
  1: '-5.0',
  2: '-4.7',
  3: '-4.3',
  4: '-4.0',
  5: '-3.7',
  6: '-3.3',
  7: '-3.0',
  8: '-2.7',
  9: '-2.3',
  10: '-2.0',
  11: '-1.7',
  12: '-1.3',
  13: '-1.0',
  14: '-0.7',
  15: '-0.3',
  16: '0',
  17: '0.3',
  18: '0.7',
  19: '1.0',
  20: '1.3',
  21: '1.7',
  22: '2.0',
  23: '2.3',
  24: '2.7',
  25: '3.0',
  26: '3.3',
  27: '3.7',
  28: '4.0',
  29: '4.3',
  30: '4.7',
  31: '5.0',
  255: 'Fixed'
}

export const CAMERA_SHUTTER_SPEED = {
  0: '1/8000',
  1: '1/6400',
  2: '1/6000',
  3: '1/5000',
  4: '1/4000',
  5: '1/3200',
  6: '1/3000',
  7: '1/2500',
  8: '1/2000',
  9: '1/1600',
  10: '1/1500',
  11: '1/1250',
  12: '1/1000',
  13: '1/800',
  14: '1/725',
  15: '1/640',
  16: '1/500',
  17: '1/400',
  18: '1/350',
  19: '1/320',
  20: '1/250',
  21: '1/240',
  22: '1/200',
  23: '1/180',
  24: '1/160',
  25: '1/125',
  26: '1/120',
  27: '1/100',
  28: '1/90',
  29: '1/80',
  30: '1/60',
  31: '1/50',
  32: '1/40',
  33: '1/30',
  34: '1/25',
  35: '1/20',
  36: '1/15',
  37: '1/12.5',
  38: '1/10',
  39: '1/8',
  40: '1/6.25',
  41: '1/5',
  42: '1/4',
  43: '1/3',
  44: '1/2.5',
  45: '1/2',
  46: '1/1.67',
  47: '1/1.25',
  48: '1.0',
  49: '1.3',
  50: '1.6',
  51: '2.0',
  52: '2.5',
  53: '3.0',
  54: '3.2',
  55: '4.0',
  56: '5.0',
  57: '6.0',
  58: '7.0',
  59: '8.0',
  65534: 'auto'
}

export const CAMERA_FOCUS_MODE = { 0: 'MF', 1: 'AFS', 2: 'AFC' }

export const CAMERA_LENS_ICON = {
  [CAMERA_LENS.THERMAL]: 'dmo-camera-ir-temp',
  [CAMERA_LENS.WIDE]: 'dmo-camera-wide',
  [CAMERA_LENS.ZOOM]: 'dmo-camera-zoom'
}

export const RTH_MODE = {
  INTELLIGENT_MODE: 0,
  PRESET_ALTITUDE: 1
}

export const RC_LOST_ACTION = {
  HOVERING: 0,
  LANDING: 1,
  RETURNING_TO_HOME: 2
}

export const COMMANDER_MODE_LOST_ACTION = {
  CONTINUE: 0,
  DEFAULT: 1
}

export const COMMANDER_FLIGHT_MODE = {
  OPTIMAL: 0,
  PRESET_FLIGHT_HEIGHT: 1
}

export const FLIGHT_SAFETY_ADVANCE_CHECK = {
  CLOSED: 0,
  OPEN: 1
}

export const TAKEOFF_STATE = {
  TASK_FINISH: 'task_finish',
  TASK_READY: 'task_ready',
  WAYLINE_CANCEL: 'wayline_cancel',
  WAYLINE_FAILED: 'wayline_failed',
  WAYLINE_OK: 'wayline_ok',
  WAYLINE_PROGRESS: 'wayline_progress'
}

export const FLY_TO_STATE = {
  TASK_FINISH: 'task_finish',
  TASK_READY: 'task_ready',
  WAYLINE_CANCEL: 'wayline_cancel',
  WAYLINE_FAILED: 'wayline_failed',
  WAYLINE_OK: 'wayline_ok',
  WAYLINE_PROGRESS: 'wayline_progress'
}

export const DRONE_MODE_CODE = {
  STANDBY: 0,
  TAKEOFF_PREPARATION: 1,
  TAKEOFF_PREPARATION_COMPLETED: 2,
  MANUAL_FLIGHT: 3,
  AUTOMATIC_TAKEOFF: 4,
  WAYLINE_FLIGHT: 5,
  PANORAMIC_PHOTOGRAPHY: 6,
  INTELLIGENT_TRACKING: 7,
  ADS_B_AVOIDANCE: 8,
  AUTO_RETURNING_TO_HOME: 9,
  AUTOMATIC_LANDING: 10,
  FORCED_LANDING: 11,
  THREE_BLADE_LANDING: 12,
  UPGRADING: 13,
  NOT_CONNECTED: 14,
  APAS: 15,
  VIRTUAL_STICK_STATE: 16,
  LIVE_FLIGHT_CONTROLS: 17,
  AIRBORNE_RTK_FIXING_MODE: 18,
  DOCK_ADDRESS_SELECTING: 19
}

export const DOCK_MODE_CODE = {
  IDLE: 0,
  ON_SITE_DEBUGGING: 1,
  REMOTE_DEBUGGING: 2,
  FIRMWARE_UPGRADE_IN_PROGRESS: 3,
  IN_OPERATION: 4,
  TO_BE_CALIBRATED: 5
}

export const DRONE_MODE_CODE_REASON = {
  0: 'No meaning',
  1: 'Insufficient battery power (return, landing)',
  2: 'Insufficient battery voltage (return, landing)',
  3: 'Severely low voltage (return, landing)',
  4: 'Requested by remote controller buttons (takeoff, return, landing)',
  5: 'Requested by App (takeoff, return, landing)',
  6: 'Loss of remote controller signal (return, landing, hover)',
  7: 'Triggered by external devices such as navigation, SDK, etc. (takeoff, return, landing)',
  8: 'Entered the dock GEO Zone (landing)',
  9: 'Although a return was triggered, it was too close to the Home point (landing)',
  10: 'Although a return was triggered, it was too far from the Home point (landing)',
  11: 'Requested when executing waypoint missions (takeoff)',
  12: 'Requested after reaching above the Home point in the return phase (landing)',
  13: "Continued descent after the aircraft's height dropped to 0.7m from the ground (second-stage descent limit) leading to (landing)",
  14: 'Forced breakthrough of low altitude protection by devices like App, SDK (landing)',
  15: 'Requested due to passing flights in the vicinity (returning, landing)',
  16: 'Requested due to height control failure (return, landing)',
  17: 'Entered after intelligent low battery return (landing)',
  18: 'AP controls the flight mode (manual flight)',
  19: 'Hardware abnormally (return, landing)',
  20: 'End of anti-collision protection (landing)',
  21: 'Return canceled (hover)',
  22: 'Encountered obstacles during the return (landing)',
  23: 'Triggered by strong winds in the dock scene (return)'
}

export const CONCISE_DRONE_MODE_CODE_REASON = {
  0: '',
  1: 'Low Battery',
  2: 'Low Battery Voltage',
  3: 'Critical Battery Voltage',
  4: 'RC Command',
  6: 'Loss of Signal Action',
  7: 'External Trigger',
  8: 'Entered Dock Geo Zone',
  9: 'RTH Too Close to Home',
  10: 'RTH Too Far From Home',
  15: 'Flight Passing in Vicinity',
  16: 'Height Control Failure',
  17: 'Critical Battery Level',
  19: 'Hardware Abnormal',
  20: 'Anti-Collision Protection Disabled',
  21: 'RTH Cancelled',
  22: 'Obstacles Encountered During RTH',
  23: 'Strong Wind Near Dock'
}

export const RTK_FIXING_STATE = {
  NOT_STARTED: 0,
  FIXING: 1,
  FIXING_SUCCESSFUL: 2,
  FIXING_FAILED: 3
}

export const CAMERA_STORAGE_TYPE = {
  CURRENT: 'current',
  VISION: 'vision',
  IR: 'ir'
}

export const DOCK_AIR_CONDITIONER_STATE = {
  IDLE_MODE: 0,
  COOLING_MODE: 1,
  HEATING_MODE: 2,
  DEHUMIDIFICATION_MODE: 3,
  COOLING_EXIT_MODE: 4,
  HEATING_EXIT_MODE: 5,
  DEHUMIDIFICATION_EXIT_MODE: 6,
  COOLING_READY_MODE: 7,
  HEATING_READY_MODE: 8,
  DEHUMIDIFICATION_READY_MODE: 9,
  PREPARING_FOR_AIR_COOLING: 10,
  AIR_COOLING_IN_PROGRESS: 11,
  AIR_COOLING_EXITING: 12,
  PREPARING_FOR_DEFOGGER: 13,
  DEFOGGER_IN_PROGRESS: 14,
  DEFOGGER_EXITING: 15
}

export const DOCK_ALARM_STATE = {
  DISABLE: 0,
  ENABLE: 1
}

export const DOCK_BATTERY_STORE_MODE = { SCHEDULE_MODE: 1, STANDBY_MODE: 2 }

export const KB_IN_GB = 1048576

export const CONTROL_AUTHORITY_DOMAIN = {
  PAYLOAD: 'payload',
  FLIGHT: 'flight'
}

export const DOCK_RAINFALL = {
  NO_RAIN: 0,
  LIGHT_RAIN: 1,
  MODERATE_RAIN: 2,
  HEAVY_RAIN: 3
}

export const CAMERA_GIMBAL_RESET_MODE = {
  RESET: 0,
  DOWNWARD: 1,
  GIMBAL_PAN_RECENTER: 2,
  GIMBAL_PITCH_DOWNWARD: 3
}

export const GEOFENCE_TYPE = {
  FLY_ZONE: 'dfence',
  NO_FLY_ZONE: 'nfz'
}

export const GEOZONE_SYNC_STATUS = {
  FAIL: 'fail',
  SWITCH_FAIL: 'switch_fail',
  SYNCHRONIZED: 'synchronized',
  SYNCHRONIZING: 'synchronizing',
  WAIT_SYNC: 'wait_sync'
}

export const PITCH_ANGLE_CHANGE_DIRECTION = {
  UP: 'up',
  DOWN: 'down'
}

export const AUTHORITY_REQUEST_EXPIRE_TIME = 1000 * 60 // 60 seconds
